import { useCallback, useEffect } from "react";

import { atom, useAtom } from "jotai";
import {
  atomFamily,
  atomWithStorage,
  useAtomCallback,
  useAtomValue,
} from "jotai/utils";
import deepEqual from "fast-deep-equal";

import { DataFile } from "interfaces";
import {
  MissingTreatment,
  missingTIsFill,
  missingTIsSubstitute,
} from "interfaces/data-files/data-file-create";

import { useGetFileColumns } from "./file-api";
import { useFetchColumnProfile } from "./column-insights";
import { retainedColumns, droppedColumns } from "./keep-drop";
import { columnType, fileColTypes } from "./column-types";

interface TypeBasedMissingTreatment<T> {
  value?: T;
  "substitute-params"?: {
    substitute: string;
    treatment: TypeBasedMissingTreatment<T>;
  };
}

interface Spec {
  fileId: string;
  column: string;
}

const missingTreatmentOption = atomFamily(
  ({ fileId, column }: Spec) =>
    atomWithStorage<MissingTreatment["treatment"]>(
      `data-files/missing-value-treatment/treatment-v2/${fileId}/${column}`,
      null
    ),
  deepEqual
);

export const useSetBulkMissingTreatment = (
  fileId: string,
  option: "ignore" | "drop-rows",
  columns?: string[]
) => {
  const fileColumns = useGetFileColumns(fileId);
  if (!columns) {
    columns = fileColumns;
  }
  return useAtomCallback(
    useCallback(
      (_, set) => {
        columns.forEach((c) =>
          set(missingTreatmentOption({ fileId, column: c }), option)
        );
      },
      [fileId, option, columns]
    )
  );
};

const numericTreatment = atomFamily(
  ({ fileId, column }: Spec) =>
    atomWithStorage<TypeBasedMissingTreatment<number>>(
      `data-files/missing-value-treatment/numeric/${fileId}/${column}`,
      {}
    ),
  deepEqual
);

const charTreatment = atomFamily(
  ({ fileId, column }: Spec) =>
    atomWithStorage<TypeBasedMissingTreatment<string>>(
      `data-files/missing-value-treatment/char/${fileId}/${column}`,
      {}
    ),
  deepEqual
);

const dateTreatment = atomFamily(
  ({ fileId, column }: Spec) =>
    atomWithStorage<TypeBasedMissingTreatment<Date>>(
      `data-files/missing-value-treatment/date/${fileId}/${column}`,
      {}
    ),
  deepEqual
);

export const colMissingValueTreatment = atomFamily(
  ({ fileId, column }: Spec) =>
    atom<
      | TypeBasedMissingTreatment<number>
      | TypeBasedMissingTreatment<string>
      | TypeBasedMissingTreatment<Date>,
      | TypeBasedMissingTreatment<number>
      | TypeBasedMissingTreatment<string>
      | TypeBasedMissingTreatment<Date>
    >(
      (get) => {
        switch (get(columnType({ fileId, column }))) {
          case "numeric":
            return get(numericTreatment({ fileId, column }));
          case "char":
            return get(charTreatment({ fileId, column }));
          case "date":
            return get(dateTreatment({ fileId, column }));
          default:
            return {};
        }
      },
      (get, set, treatment) => {
        switch (get(columnType({ fileId, column }))) {
          case "numeric":
            const nt = get(numericTreatment({ fileId, column }));
            set(numericTreatment({ fileId, column }), {
              ...nt,
              ...treatment,
            } as TypeBasedMissingTreatment<number>);
          case "char":
            const ct = get(charTreatment({ fileId, column }));
            set(charTreatment({ fileId, column }), {
              ...ct,
              ...treatment,
            } as TypeBasedMissingTreatment<string>);
          case "date":
            const dt = get(dateTreatment({ fileId, column }));
            set(dateTreatment({ fileId, column }), {
              ...dt,
              ...treatment,
            } as TypeBasedMissingTreatment<Date>);
        }
      }
    ),
  deepEqual
);

export const columnMissingTreatmentSpec = atomFamily(
  ({ fileId, column }: Spec) =>
    atom<MissingTreatment, MissingTreatment>(
      (get) => {
        const option = get(missingTreatmentOption({ fileId, column }));
        if (option === "drop-rows" || option === "ignore") {
          return { column, treatment: option };
        }
        const treatment = get(colMissingValueTreatment({ fileId, column }));
        return { column, treatment: option, ...treatment } as MissingTreatment;
      },
      (get, set, newValue) => {
        const option = get(missingTreatmentOption({ fileId, column }));
        const treatment = (newValue as MissingTreatment).treatment;

        if (treatment !== option) {
          set(missingTreatmentOption({ fileId, column }), treatment);
          if (treatment === "drop-rows" || treatment === "ignore") {
            return;
          }
        }

        const {
          column: c,
          treatment: t,
          ...rest
        } = newValue as MissingTreatment;
        set(colMissingValueTreatment({ fileId, column }), rest);
      }
    ),
  deepEqual
);

export const fileMissingTreatmentSpec = atomFamily((fileId: string) =>
  atom<MissingTreatment[]>((get) => {
    const columns = get(retainedColumns(fileId));
    return columns.map((column) =>
      get(columnMissingTreatmentSpec({ fileId, column }))
    );
  })
);

export const getInitMissingTreatment = (
  colType: "char" | "numeric" | "date",
  stats: DataFile.ColumnStatsInterface
): {
  option: MissingTreatment["treatment"];
  value?: number | string | Date;
} => {
  if (stats.count === stats.size) {
    return { option: "ignore" };
  }
  const value =
    colType === "char"
      ? "UNKNOWN/MISSING"
      : colType === "numeric"
      ? stats["default-values"]
      : new Date(stats["default-values"]);

  return {
    value,
    option: stats.count / stats.size >= 0.99 ? "drop-rows" : "fill-value",
  };
};

interface ValidationError {
  value?: string;
  substituteParams?: {
    substitute?: string;
    treatment?: { treatment?: string; value?: string };
  };
}

export const useInitMissingTreatment = (fileId: string, column: string) => {
  const colType = useAtomValue(columnType({ fileId, column }));
  const { data } = useFetchColumnProfile(fileId, column, colType);

  const [option, setOption] = useAtom(
    missingTreatmentOption({ fileId, column })
  );
  const [currValue, setValues] = useAtom(
    colMissingValueTreatment({ fileId, column })
  );

  useEffect(() => {
    if ((option === null || option === undefined) && data) {
      const { option, value } = getInitMissingTreatment(colType, data.stats);
      setOption(option);
      if (
        value !== null &&
        value !== undefined &&
        currValue !== null &&
        currValue !== undefined
      ) {
        // @ts-ignore
        setValues({ value: value });
      }
    }
  }, [data, option, currValue, setOption, setValues]);
};

export const validateMissingTreatment = (
  spec: MissingTreatment,
  types: DataFile.UserDefinedTypes,
  droppedColumns: string[]
): ValidationError | undefined => {
  const { column } = spec;
  if ((droppedColumns || []).includes(column)) {
    return;
  }

  let err: {
    value?: string;
    substituteParams?: {
      substitute?: string;
      treatment?: { treatment?: string; value?: string };
    };
  } = {};

  if (missingTIsFill(spec)) {
    const { value } = spec;
    if (value === undefined || value === null || value === "") {
      err.value = "Invalid Fill Value!!!";
    } else if (types[column] === "numeric") {
      if (isNaN(value as number)) {
        err.value = "Invalid Fill Value!!!";
      }
    } else if (types[column] === "date") {
      if (isNaN(new Date(value).valueOf())) {
        err.value = "Invalid Fill Value!!!";
      }
    }
  }

  if (missingTIsSubstitute(spec)) {
    const substituteParams = spec["substitute-params"];
    if (!substituteParams) {
      err.substituteParams = { substitute: "MUST be passed" };
    } else {
      const { substitute } = substituteParams;
      if (!substitute) {
        err.substituteParams = { substitute: "MUST be passed" };
      } else if (droppedColumns.includes(substitute)) {
        err.substituteParams = { substitute: "This column was DROPPED!!!" };
      } else {
        if (substituteParams.treatment?.treatment === "fill-value") {
          // @ts-ignore
          const subValue = substituteParams.treatment?.value;
          if (subValue === undefined || subValue === null || subValue === "") {
            err.substituteParams = {
              ...(err.substituteParams || {}),
              treatment: { value: "Invalid Fill Value!!!" },
            };
          } else if (types[column] === "numeric") {
            if (isNaN(subValue as number)) {
              err.substituteParams = {
                ...(err.substituteParams || {}),
                treatment: { value: "Invalid Fill Value!!!" },
              };
            }
          } else if (types[column] === "date") {
            if (isNaN(new Date(subValue).valueOf())) {
              err.substituteParams = {
                ...(err.substituteParams || {}),
                treatment: { value: "Invalid Fill Value!!!" },
              };
            }
          }
        }
      }
    }
  }

  return Object.keys(err).length === 0 ? undefined : err;
};

export const useValidateColumnMissingTreatment = (
  fileId: string,
  column: string
) => {
  const dropped = useAtomValue(droppedColumns(fileId));
  const spec = useAtomValue(columnMissingTreatmentSpec({ fileId, column }));
  const colTypes = useAtomValue(fileColTypes(fileId));
  return validateMissingTreatment(spec, colTypes, dropped);
};
